// Builds a deck.gl LineLayer tying each container marker back to the stop it
// belongs to.
//
// containerOffsets fans co-located containers away from their parent stop so the
// pills don't overlap, and the offset sliders push them further still. That
// makes the map readable and the ownership unreadable — a container can sit well
// clear of the stop it serves with nothing saying which one that is. This is the
// other half of that algorithm: draw the line the offset implies.
import { LineLayer } from '@deck.gl/layers'

import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'

import { buildContainerTetherData } from './containerTetherData'

import type { ContainerTether, TetherFeature } from './containerTetherData'
import type { Layer } from '@deck.gl/core'
import type { Theme } from '@mui/material/styles'

const TETHER_WIDTH_PX = 1.5
// Subordinate to the markers it connects: readable when traced deliberately,
// quiet enough that a dense route doesn't turn into a web.
const TETHER_ALPHA = 110

export type ContainerTetherLayerArgs = {
  containerFeatures?: TetherFeature[]
  stopFeatures?: TetherFeature[]
  theme: Theme
  id?: string
}

export const buildContainerTetherLayer = ({
  containerFeatures = [],
  stopFeatures = [],
  theme,
  id = 'container-tethers',
}: ContainerTetherLayerArgs): Layer | null => {
  const data = buildContainerTetherData(containerFeatures, stopFeatures)
  if (data.length === 0) return null

  const [red, green, blue] = hexToRgb(theme.palette.text.secondary)

  return new LineLayer<ContainerTether>({
    id,
    data,
    pickable: false,
    widthUnits: 'pixels',
    getSourcePosition: d => d.source,
    getTargetPosition: d => d.target,
    getColor: [red, green, blue, TETHER_ALPHA],
    getWidth: TETHER_WIDTH_PX,
  })
}
